import { debounce } from '../utils/helpers';

const BLOCK_SELECTOR = [
	'.student-spending-tabs .levit_tabs_block[data-id="student-spending"]',
	'.tabs-primary.levit_tabs_block',
	'.tabs-primary .levit_tabs_block',
].join(', ');

const SCOPED_TAB_ITEMS = ':scope > .tabs_list_item, :scope > .wp-block-custom-tab-item';
const TAB_ITEMS_ANY = '.tabs_list_item, .wp-block-custom-tab-item';

const DEBOUNCE_MS = 100;
const BOOT_DELAY_MS = 300;
const FLUSH_THRESHOLD_PX = 8;
const SCROLL_TOLERANCE_PX = 1;

const registry = new WeakMap();

function wrapSelectTrigger(trigger) {
	if (!trigger || trigger.querySelector('.levit-tabs__select-content')) return;

	const text = document.createElement('span');
	text.className = 'levit-tabs__select-text';

	for (const node of [...trigger.childNodes]) {
		text.appendChild(node);
	}

	const content = document.createElement('span');
	content.className = 'levit-tabs__select-content';
	content.appendChild(text);
	trigger.appendChild(content);
}

function updateSelectLabel(trigger, html) {
	if (!trigger) return;
	wrapSelectTrigger(trigger);
	const text = trigger.querySelector('.levit-tabs__select-text');
	if (text) {
		text.innerHTML = html;
	} else {
		trigger.innerHTML = html;
		wrapSelectTrigger(trigger);
	}
}

function syncSelectOptions(dropdown) {
	dropdown.querySelectorAll(TAB_ITEMS_ANY).forEach((item) => {
		item.classList.add('levit-tabs__select-option');
		item.setAttribute('role', 'option');
		item.setAttribute('aria-selected', String(item.classList.contains('active')));
	});
}

function closeSelect(select) {
	select.classList.remove('open');
	select.querySelector('.tabs_list_container')?.classList.remove('is-open');
	select.querySelector(':scope > span')?.setAttribute('aria-expanded', 'false');
}

function enhanceSelect(select) {
	if (select.dataset.enhanced) return;
	select.dataset.enhanced = 'true';

	const trigger = select.querySelector(':scope > span');
	const dropdown = select.querySelector('.tabs_list_container');
	if (!trigger || !dropdown) return;

	trigger.setAttribute('role', 'button');
	trigger.setAttribute('aria-haspopup', 'listbox');
	dropdown.classList.add('levit-tabs__select-dropdown');
	dropdown.setAttribute('role', 'listbox');

	wrapSelectTrigger(trigger);
	syncSelectOptions(dropdown);

	const syncOpen = () => {
		const isOpen = select.classList.contains('open');
		trigger.setAttribute('aria-expanded', String(isOpen));
		dropdown.classList.toggle('is-open', isOpen);
	};

	syncOpen();

	trigger.addEventListener('click', (e) => {
		e.stopPropagation();
		select.classList.toggle('open');
		syncOpen();
	});

	new MutationObserver(syncOpen).observe(select, {
		attributes: true,
		attributeFilter: ['class'],
	});
}

class StudentSpendingTabsBlock {
	constructor(block) {
		this.block = block;
		this.ready = false;
		this._observing = false;
		this._tryBind();
	}

	_tryBind() {
		if (this.ready) return;

		const { block } = this;
		const contentContainer = block.querySelector('.tabs_content_container');
		const navList = block.querySelector('.tabs_content_all .tabs_list_container');
		if (!contentContainer || !navList) return;

		if (!this._navFillBound) {
			this._navFillBound = true;
			this._updateNavFill();
			this._bindScroll();
			this._bindResize();
		}

		const panels = Array.from(contentContainer.children).filter((c) =>
			c.classList.contains('wp-block-group'),
		);
		const tabItems = Array.from(navList.querySelectorAll(SCOPED_TAB_ITEMS));
		if (!panels.length || !tabItems.length || panels.length !== tabItems.length) return;

		this.panels = panels;
		this.tabItems = tabItems;
		this.ready = true;

		block.querySelectorAll('.tabs_list_select').forEach(enhanceSelect);
		this._setActive(0);
		this._bindClicks();
	}

	_setActive(index) {
		const { block, panels, tabItems } = this;

		block.querySelectorAll('.tabs_list_container').forEach((list) => {
			list.querySelectorAll(SCOPED_TAB_ITEMS).forEach((item, i) => {
				item.classList.toggle('active', i === index);
			});
		});

		panels.forEach((panel, i) => panel.classList.toggle('active', i === index));

		block.querySelectorAll('.tabs_list_select').forEach((select) => {
			const trigger = select.querySelector(':scope > span');
			if (trigger && tabItems[index]) {
				updateSelectLabel(trigger, tabItems[index].innerHTML);
			}
			closeSelect(select);
			const dropdown = select.querySelector('.tabs_list_container');
			if (dropdown) syncSelectOptions(dropdown);
		});

		this._updateNavFill();
	}

	_getTabIndex(item) {
		const list = item.closest('.tabs_list_container');
		if (!list || !this.block.contains(list)) return -1;
		return Array.from(list.querySelectorAll(SCOPED_TAB_ITEMS)).indexOf(item);
	}

	_bindClicks() {
		this.block.addEventListener('click', (e) => {
			const tabItem = e.target.closest(TAB_ITEMS_ANY);
			if (!tabItem || !this.block.contains(tabItem)) return;
			const index = this._getTabIndex(tabItem);
			if (index < 0) return;
			e.preventDefault();
			this._setActive(index);
		});

		document.addEventListener('click', (e) => {
			if (!this.block.contains(e.target)) {
				this.block.querySelectorAll('.tabs_list_select.open').forEach(closeSelect);
			}
		});
	}

	_updateNavFill() {
		const scrollArea = this.block.querySelector('.tabs_content_all');
		const nav = scrollArea?.querySelector(':scope > .tabs_list_container');
		const content = this.block.querySelector('.tabs_content_container');
		if (!scrollArea || !nav || !content) return;

		const lastTab = nav.querySelector(':scope > :last-child');
		if (!lastTab) return;

		const areaRight = scrollArea.getBoundingClientRect().right;
		const lastRight = lastTab.getBoundingClientRect().right;
		const isScrollable = scrollArea.scrollWidth > scrollArea.clientWidth + SCROLL_TOLERANCE_PX;
		const isFlushed = lastRight >= areaRight - FLUSH_THRESHOLD_PX;

		content.classList.toggle('tabs-nav-fills-width', isScrollable || isFlushed);
	}

	_bindScroll() {
		const scrollArea = this.block.querySelector('.tabs_content_all');
		scrollArea?.addEventListener('scroll', () => this._updateNavFill(), { passive: true });
	}

	_bindResize() {
		window.addEventListener('resize', debounce(() => this._updateNavFill(), DEBOUNCE_MS));
	}
}

export default class StudentSpendingTabs {
	static init() {
		const boot = () => {
			document.querySelectorAll(BLOCK_SELECTOR).forEach((block) => {
				let instance = registry.get(block);
				if (!instance) {
					instance = new StudentSpendingTabsBlock(block);
					registry.set(block, instance);
				}

				if (instance.ready) {
					instance._updateNavFill();
					return;
				}

				if (!instance._observing) {
					instance._observing = true;
					const obs = new MutationObserver(() => {
						instance._tryBind();
						if (instance.ready) {
							instance._updateNavFill();
							obs.disconnect();
						}
					});
					obs.observe(block, { childList: true, subtree: true });
				}
			});
		};

		boot();
		requestAnimationFrame(boot);
		setTimeout(boot, BOOT_DELAY_MS);
	}
}
